import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Circle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shared online/offline toggle used by DoctorDashboard and
 * PharmacistDashboard. Persists via PUT /api/doctors/me/online, which
 * accepts both roles server-side. Optimistically flips the local state
 * and rolls back if the request fails.
 */
export const OnlineToggle = () => {
  const { user, checkAuth } = useAuth();
  const { t } = useLanguage();
  const [isOnline, setIsOnline] = useState(!!user?.is_online);

  // Keep the switch in sync when AuthContext refreshes the user.
  useEffect(() => { setIsOnline(!!user?.is_online); }, [user?.is_online]);

  const handleToggle = async (checked) => {
    setIsOnline(checked);
    try {
      await axios.put(
        `${API}/doctors/me/online`,
        { is_online: checked },
        { withCredentials: true }
      );
      checkAuth();
    } catch (_e) {
      // Roll back optimistic flip so the switch reflects the real state.
      setIsOnline(!checked);
    }
  };

  return (
    <Card className="p-4 flex items-center gap-3" data-testid="online-toggle-card">
      <Circle
        size={12}
        className={isOnline ? 'fill-green-500 text-green-500' : 'fill-gray-400 text-gray-400'}
      />
      <div className="flex-1 min-w-0">
        <Label htmlFor="online-switch" className="cursor-pointer font-semibold">
          {isOnline
            ? t('متاح الآن', 'Available', 'Disponible')
            : t('غير متاح', 'Not available', 'Non disponible')}
        </Label>
        <p className="text-[11px] text-gray-500 mt-0.5">
          {isOnline
            ? t('المرضى يمكنهم الاتصال بك وحجز موعد', 'Patients can call and book you', 'Les patients peuvent vous contacter')
            : t('لن يتمكن المرضى من الاتصال أو الحجز', 'Patients cannot call or book you', 'Les patients ne peuvent pas réserver')}
        </p>
      </div>
      <Switch
        id="online-switch"
        checked={isOnline}
        onCheckedChange={handleToggle}
        data-testid="online-switch"
      />
    </Card>
  );
};

export default OnlineToggle;
